import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { enqueueSnackbar } from 'notistack'
import { apiInstance } from 'services/api'
import { useMasquaradeToken } from 'services/masquarade/masquarade.hooks'
import { useAccessToken } from 'services/token/token.hooks'

interface BankAccountResponse {
  hasForeignBankAccount: boolean
  bankAccountIban: string | null
  bankAccountSwift: string | null
  bankName: string | null
  bankAccountData: string | null
}

// Note: apiInstance automatically converts snake_case to camelCase
// so we only need the camelCase interface

export const useUserBankAccount = () => {
  const queryClient = useQueryClient()
  const { accessToken } = useAccessToken()
  const { masquaradeToken } = useMasquaradeToken()

  const { data: userBankAccount, isLoading, error } = useQuery({
    queryKey: ['userBankAccount', masquaradeToken],
    queryFn: () =>
      apiInstance<BankAccountResponse>({
        url: `/users/profile/bank-account/`,
        method: 'GET',
        headers: {
          authorization: `Bearer ${accessToken}`,
          'mask-authorization': !!masquaradeToken ? `Token ${masquaradeToken}` : undefined
        }
      }).then(({ data }) => data as BankAccountResponse),
    enabled: !!accessToken
  })

  const usersProfileBankAccountPartialUpdate = useMutation({
    mutationFn: (bank: Partial<BankAccountResponse>) =>
      apiInstance<BankAccountResponse>({
        url: `/users/profile/bank-account/`,
        method: 'PATCH',
        data: bank,
        headers: {
          authorization: `Bearer ${accessToken}`,
          'mask-authorization': !!masquaradeToken ? `Token ${masquaradeToken}` : undefined
        }
      }).then(({ data }) => data),
    onSuccess: () => {
      // Invalidate and refetch user bank account data
      queryClient.invalidateQueries({ queryKey: ['userBankAccount', masquaradeToken] })
      enqueueSnackbar('Dane bankowe zostały zaktualizowane', {
        variant: 'success'
      })
    },
    onError: (error) => {
      enqueueSnackbar('Wystąpił błąd', {
        variant: 'error' as any,
        bugDetails: (error as any)?.response?.data
          ? JSON.stringify((error as any)?.response?.data)
          : undefined
      })
    }
  })

  return {
    userBankAccount,
    isLoading,
    error,
    usersBankAccountUpdate: usersProfileBankAccountPartialUpdate
  }
}
